import { useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useHypoteseListe } from '../api/useHypoteseBehandling.ts';
import type { HypothesisConfig } from '../api/useHypotheses.ts';
import { useMoeteSkriving, type DecisionMeeting, type DecisionMeetingItem, type MeetingDecisionUpdate, type MeetingEvent, type MeetingOutcome } from '../api/useMoeter.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { tokenVar } from '../status/status.ts';
import { Fot, Tekstknapp, Varsel } from './deler.tsx';
import { nummerTekst } from './Kvittering.tsx';
import { navnAv, stor } from './lesning.ts';
import { aktiveRader, alleRader, fristKort, gjeldende, innsendere, klokke, kodeNavn, malFor, moeteNavn, moeteStatus, navneliste, somOrd, svarVed, tallTekst, utfallKort, utfallOppforing, utfallPille } from './moeteLesning.ts';
import { aapneSkjermen } from './moeteSynk.ts';
import { kortDatoAv } from './setning.ts';
import { UtfallFelter, type Utfallsverdier } from './UtfallFelter.tsx';

/**
 * Beslutningsloggen (H3.2, W9–W11): den varige siden per møte. Samme side før og etter, så
 * beslutningstakeren og den som registrerte ser det samme — med hans knapper mens møtet venter på
 * ham («Endre», «Gi videre …», «Bekreft alle fem»), og etterpå med «Åpne» per rad, hva som gikk ut
 * og hva som gjenstår, «Endret av …» der han endret, og møtets egen tidslinje.
 *
 * Notatet fra kjøringen står aldri her (Kh5), og tallene bare når tjeneren gir dem (leser og opp og
 * beslutningstakeren, P4).
 */
export function Beslutningslogg({ m, config, megId, leserPluss, smal, onKvittering }: { m: DecisionMeeting; config: HypothesisConfig | undefined; megId: string | undefined; leserPluss: boolean; smal: boolean; onKvittering?: (tekst: string) => void }) {
  const skriv = useMoeteSkriving(m.id);
  const beslutter = m.decider.id === megId;
  const registrar = m.registeredBy.id === megId;
  const venter = m.status === 'venter_bekreftelse';
  const kanBekrefte = beslutter && venter;
  const rader = useMemo(() => alleRader(m), [m]);
  const aktive = useMemo(() => aktiveRader(m), [m]);
  const [endrer, setEndrer] = useState<string | null>(null);
  const [giVidere, setGiVidere] = useState<DecisionMeetingItem | 'moete' | null>(null);
  const [begrunner, setBegrunner] = useState<DecisionMeetingItem | null>(null);
  const [mer, setMer] = useState<string | null>(null);
  const [melding, setMelding] = useState<{ tekst: string; tone: 'ok' | 'info' | 'warning'; lenke?: boolean } | null>(null);
  const status = moeteStatus(m.status);
  const pille = venter && beslutter ? (status.etikettFor ?? status.etikett) : status.etikett;
  const telling = useMemo(() => {
    const t: Record<string, number> = {};
    for (const i of aktive) {
      const o = gjeldende(i);
      if (o) t[o.outcome] = (t[o.outcome] ?? 0) + 1;
    }
    return t;
  }, [aktive]);
  const tilBekreftelse = aktive.filter((i) => i.state === 'registrert');
  const svar = svarVed(tilBekreftelse);
  const endret = rader.filter((i) => i.changedByDecider).length;
  const mangler = rader.filter((i) => i.state === 'venter_begrunnelse');
  const kanBegrunne = registrar || beslutter;
  const loggSti = leserPluss ? `/h/moter/${m.id}` : `/h/til-deg/moter/${m.id}`;

  async function bekreft() {
    setMelding(null);
    try {
      const ny = await skriv.mutateAsync({ handling: 'bekreft' });
      const s = svarVed(aktiveRader(ny).filter((i) => i.state === 'bekreftet' || i.state === 'venter_begrunnelse'));
      const venterPaa = aktiveRader(ny).filter((i) => i.state === 'venter_begrunnelse');
      const tekst = ['Beslutningene er bekreftet.', s.faar.length ? `Svar gikk ut til ${navneliste(s.faar)}.` : 'Ingen svar gikk ut.', ...venterPaa.map((i) => `Avvisningen av ${nummerTekst(i.hypothesis.number)} venter på begrunnelse fra ${navnAv(ny.registeredBy)}.`)].join(' ');
      setMelding({ tekst, tone: 'ok' });
      onKvittering?.(tekst);
    } catch (e) {
      if (erApiFeil(e) && e.code === 'meeting_not_awaiting_confirmation') {
        setMelding({ tekst: '', tone: 'info', lenke: true });
        return;
      }
      if (erApiFeil(e) && e.code === 'not_decider') {
        setMelding({ tekst: `Bare ${navnAv(m.decider)} kan bekrefte dette møtet. Du ser lista, ikke knappene.`, tone: 'warning' });
        return;
      }
      setMelding({ tekst: erApiFeil(e) ? (erNettfeil(e) ? 'Ikke bekreftet — ingen nett. Prøv igjen når nettet er tilbake.' : e.brukertekst) : 'Noe gikk galt. Prøv igjen.', tone: 'warning' });
    }
  }

  const hode = venter ? (beslutter ? 'Ingenting går ut før du bekrefter. Endre en rad, eller bekreft alle.' : `Venter på at ${navnAv(m.decider)} bekrefter. Ingenting går ut før det.`) : m.status === 'bekreftet' ? `${navnAv(m.decider)} bekreftet ${m.confirmedAt ? `${kortDatoAv(m.confirmedAt)} ${klokke(m.confirmedAt)}` : ''}${endret ? ` og endret ${endret === 1 ? 'én rad' : `${somOrd(endret)} rader`}` : ''}.` : '';

  const crumb = [
    'Møte',
    m.status === 'bekreftet' ? `registrert av ${registrar ? 'deg' : navnAv(m.registeredBy)}` : `registrert av ${navnAv(m.registeredBy)}`,
    venter && m.sentAt ? `sendt${beslutter ? ' til deg' : ''} ${kortDatoAv(m.sentAt)} ${klokke(m.sentAt)}` : null,
    m.status === 'bekreftet' && m.confirmedAt ? `bekreftet av ${beslutter ? 'deg' : navnAv(m.decider)} ${kortDatoAv(m.confirmedAt)} ${klokke(m.confirmedAt)}` : null,
  ].filter(Boolean);

  // Ved 409 er møtet hentet på nytt av mutasjonen (onSuccess kjører ikke) — siden leser det som er nå.
  const bekreftetImens = melding?.lenke;

  return (
    <>
      <div className="hb-detalj-rull">
        <div className="hb-detalj-innhold ml">
          <div className="ml-crumb">
            <span>{crumb.join(' · ')}</span>
            {kanBekrefte ? (
              <button type="button" className="panel-knapp-lenke" onClick={() => setGiVidere('moete')}>
                <Ikon navn="arrow-redo-outline" />
                Gi hele møtet videre …
              </button>
            ) : m.status === 'bekreftet' ? (
              <button type="button" className="panel-knapp-lenke" onClick={() => aapneSkjermen(m.id)}>
                <Ikon navn="open-outline" />
                Åpne skjermen i rommet
              </button>
            ) : null}
          </div>
          <h2 id="moete-h" className={smal ? "panel-skjult" : "hb-tittel"}>
            {moeteNavn(m)}
          </h2>
          <p className="ml-naa">
            <span className="panel-pille panel-pille-outline" style={{ '--pille-fg': tokenVar(status.fg), '--pille-bg': tokenVar(status.bg) } as CSSProperties} data-status={m.status}>
              <Ikon navn={status.ikon} />
              {pille}
            </span>
            {hode ? <span className="hb-dempet">{hode}</span> : null}
          </p>

          {venter && !beslutter ? (
            <p className="h-grunn ml-laas" role="note">
              <Ikon navn="lock-closed-outline" className="h-grunn-ikon" />
              <span>{`Bare ${navnAv(m.decider)} kan bekrefte dette møtet. Du ser lista, ikke knappene.`}</span>
            </p>
          ) : null}

          {melding && !bekreftetImens ? (
            <div role={melding.tone === 'ok' ? 'status' : 'alert'} className={`h-varsel ${melding.tone === 'ok' ? 'ml-kvittering' : `h-varsel-${melding.tone}`}`}>
              <Ikon navn={melding.tone === 'ok' ? 'checkmark-circle-outline' : melding.tone === 'info' ? 'information-circle-outline' : 'warning-outline'} className="h-varsel-ikon" />
              <span className="h-varsel-tekst">{melding.tekst}</span>
            </div>
          ) : null}
          {bekreftetImens ? <BekreftetImens m={m} til={loggSti} /> : null}

          {m.status === 'bekreftet' ? (
            <Utsendt m={m} mangler={mangler} kanBegrunne={kanBegrunne} onBegrunn={setBegrunner} />
          ) : null}

          <p className="ml-telling" aria-label="Utfallene">
            {(['velg', 'parker', 'avvis', 'ikke_i_dag'] as const).map((k) => (
              <span key={k} className="ml-telling-del">
                {utfallOppforing(k).etikett} <b>{telling[k] ?? 0}</b>
              </span>
            ))}
          </p>

          <ul className="ml-rader" aria-label="Beslutningene">
            {rader.map((i) => (
              <Rad
                key={i.hypothesis.id}
                i={i}
                m={m}
                config={config}
                megId={megId}
                smal={smal}
                kanEndre={kanBekrefte && i.state === 'registrert'}
                endrer={endrer === i.hypothesis.id}
                merAapen={mer === i.hypothesis.id}
                tallSynlig={Boolean(config?.scoresEnabled) && (leserPluss || beslutter)}
                onEndre={() => setEndrer(i.hypothesis.id)}
                onLukkEndre={() => setEndrer(null)}
                onGiVidere={() => {
                  setMer(null);
                  setGiVidere(i);
                }}
                onMer={() => setMer((x) => (x === i.hypothesis.id ? null : i.hypothesis.id))}
              />
            ))}
          </ul>

          {m.events.length && m.status !== 'venter_bekreftelse' ? <Hendelser m={m} megId={megId} /> : null}
        </div>
      </div>
      {kanBekrefte && !bekreftetImens ? (
        <Fot
          primarFoerst
          primar={{ label: `Bekreft alle${tilBekreftelse.length > 1 ? ` ${somOrd(tilBekreftelse.length)}` : ''}`, ikon: 'checkmark-outline', onClick: () => void bekreft(), opptatt: skriv.isPending, testid: 'ml-bekreft' }}
          venstre={
            <span className="ml-fot-tekst">
              {smal
                ? svar.faar.length
                  ? `Svar går ut nå til ${somOrd(svar.faar.length)} ${svar.faar.length === 1 ? 'innsender' : 'innsendere'}.`
                  : 'Ingen svar går ut nå.'
                : [svar.faar.length ? `Svar går ut nå til ${navneliste(svar.faar)}.` : 'Ingen svar går ut nå — alle står som «Ikke i dag».', svar.venter.length ? `${navneliste(svar.venter)} får svar når begrunnelsen er skrevet.` : null].filter(Boolean).join(' ')}
            </span>
          }
        />
      ) : null}
      {giVidere ? <GiVidereDialog m={m} rad={giVidere === 'moete' ? null : giVidere} config={config} onLukk={() => setGiVidere(null)} /> : null}
      {begrunner ? <BegrunnelseDialog m={m} rad={begrunner} config={config} onLukk={() => setBegrunner(null)} /> : null}
    </>
  );
}

/** W10b 409: bekreftet et annet sted mens siden sto åpen — siden sier det og peker til loggen. */
function BekreftetImens({ m, til }: { m: DecisionMeeting; til: string }) {
  return (
    <div role="status" className="h-varsel h-varsel-info">
      <Ikon navn="information-circle-outline" className="h-varsel-ikon" />
      <span className="h-varsel-tekst">{`${navnAv(m.decider)} bekreftet møtet${m.confirmedAt ? ` ${kortDatoAv(m.confirmedAt)} ${klokke(m.confirmedAt)}` : ''} mens du så på.`}</span>
      <Link to={til} className="panel-knapp-lenke">
        Åpne beslutningsloggen
      </Link>
    </div>
  );
}

/** W11: øverst én rad som sier hva som gikk ut og hva som gjenstår, med veien dit. */
function Utsendt({ m, mangler, kanBegrunne, onBegrunn }: { m: DecisionMeeting; mangler: DecisionMeetingItem[]; kanBegrunne: boolean; onBegrunn: (i: DecisionMeetingItem) => void }) {
  const fikk = svarVed(aktiveRader(m).filter((i) => i.state === 'bekreftet' && i.transitionAt));
  const dato = m.confirmedAt ? ` ${kortDatoAv(m.confirmedAt)}` : '';
  const tekst = [fikk.faar.length ? `Svar gikk ut${dato} til ${navneliste(fikk.faar)}.` : 'Ingen svar gikk ut.', ...mangler.map((i) => `${navneliste(innsendere(i.hypothesis))} får svar når begrunnelsen for ${nummerTekst(i.hypothesis.number)} er skrevet.`)].join(' ');
  return (
    <div className="ml-utsendt" data-testid="ml-utsendt">
      <Ikon navn="send-outline" />
      <span>{tekst}</span>
      {kanBegrunne
        ? mangler.map((i) => (
            <button key={i.hypothesis.id} type="button" className="panel-knapp-lenke" onClick={() => onBegrunn(i)}>
              <Ikon navn="create-outline" />
              {`Skriv begrunnelsen for ${nummerTekst(i.hypothesis.number)} …`}
            </button>
          ))
        : null}
    </div>
  );
}

function Rad({
  i,
  m,
  config,
  megId,
  smal,
  kanEndre,
  endrer,
  merAapen,
  tallSynlig,
  onEndre,
  onLukkEndre,
  onGiVidere,
  onMer,
}: {
  i: DecisionMeetingItem;
  m: DecisionMeeting;
  config: HypothesisConfig | undefined;
  megId: string | undefined;
  smal: boolean;
  kanEndre: boolean;
  endrer: boolean;
  merAapen: boolean;
  tallSynlig: boolean;
  onEndre: () => void;
  onLukkEndre: () => void;
  onGiVidere: () => void;
  onMer: () => void;
}) {
  const h = i.hypothesis;
  const nr = nummerTekst(h.number);
  const o = gjeldende(i);
  const u = o ? utfallOppforing(o.outcome) : null;
  const beslutterSer = m.decider.id === megId;
  const bekreftet = i.state === 'bekreftet' || i.state === 'venter_begrunnelse';
  const tatt = i.state === 'tatt_ut' || i.state === 'gitt_videre';
  const fra = navneliste(innsendere(h));
  const videreTil = i.state === 'gitt_videre' ? m.events.find((e) => e.kind === 'gitt_videre' && e.hypothesisId === h.id)?.to : undefined;

  return (
    <li className={`ml-rad${tatt ? ' ml-rad-tatt' : ''}`} data-testid={`logg-rad-${h.number}`} style={u ? ({ '--ml-tone': tokenVar(u.fg) } as CSSProperties) : undefined}>
      <div className="ml-rad-hode">
        <Ikon navn={u ? u.ikon : 'ellipse-outline'} className="ml-rad-ikon" />
        <span className="ml-rad-tittel">
          <b>{h.title}</b>
          <span className="hb-dempet">{smal ? '' : ` · ${nr} · fra ${fra}`}</span>
        </span>
        <span className="ml-rad-handlinger">
          {kanEndre && !endrer ? (
            <>
              <button type="button" className="panel-knapp-lenke" aria-label={`Endre ${nr}`} onClick={onEndre}>
                Endre
              </button>
              {smal ? (
                <span className="ml-mer">
                  <button type="button" className="panel-knapp-lenke" aria-label={`Mer for ${nr}`} aria-expanded={merAapen} onClick={onMer}>
                    <Ikon navn="ellipsis-horizontal-outline" />
                  </button>
                  {merAapen ? (
                    <span className="ml-mer-meny">
                      <button type="button" className="panel-knapp-lenke" aria-label={`Gi ${nr} videre …`} onClick={onGiVidere}>
                        Gi videre …
                      </button>
                    </span>
                  ) : null}
                </span>
              ) : (
                <button type="button" className="panel-knapp-lenke" aria-label={`Gi ${nr} videre …`} onClick={onGiVidere}>
                  Gi videre …
                </button>
              )}
            </>
          ) : bekreftet && h.number !== undefined ? (
            <Link to={`/h/registeret/${h.number}`} className="panel-knapp-lenke" aria-label={`Åpne ${nr}`}>
              Åpne
            </Link>
          ) : null}
        </span>
      </div>
      {i.state === 'tatt_ut' ? (
        <p className="ml-rad-linje hb-dempet">Tatt ut — står ikke lenger i Vurderes</p>
      ) : i.state === 'gitt_videre' ? (
        <p className="ml-rad-linje hb-dempet">
          <Ikon navn="arrow-redo-outline" />
          {`Gitt videre til ${navnAv(videreTil)}`}
        </p>
      ) : o && u ? (
        <p className="ml-rad-linje">
          <span className={`panel-pille panel-pille-outline ml-utfall${o.outcome === 'ikke_i_dag' ? ' ml-utfall-noeytral' : ''}`} style={{ '--pille-fg': tokenVar(u.fg), '--pille-bg': tokenVar(u.bg) } as CSSProperties}>
            <Ikon navn={u.ikon} />
            {utfallPille(o)}
          </span>
          <UtfallDetalj i={i} o={o} m={m} config={config} bekreftet={bekreftet} />
        </p>
      ) : (
        <p className="ml-rad-linje hb-dempet">Ikke registrert</p>
      )}
      {i.changedByDecider && i.registered ? (
        <p className="ml-rad-linje ml-endret">
          <Ikon navn="create-outline" />
          <span className="ml-tekst">{beslutterSer ? `Endret av deg — ${navnAv(m.registeredBy)} registrerte «${utfallKort(i.registered, config)}»` : `Endret av ${navnAv(m.decider)} — registrert i møtet som «${utfallKort(i.registered, config)}»`}</span>
        </p>
      ) : null}
      {o?.scores && tallSynlig ? (
        <p className="ml-rad-linje ml-tall" data-testid={`ml-tall-${h.number}`}>
          <Ikon navn="stats-chart-outline" />
          <span className="ml-tekst">{smal ? tallTekst(o.scores, true) : `${tallTekst(o.scores) ?? ''} · ${
            o.scores.setBy.id === m.decider.id
              ? beslutterSer && m.status === 'venter_bekreftelse'
                ? 'du endret'
                : `${navnAv(m.decider)} ved bekreftelsen`
              : beslutterSer && m.status === 'venter_bekreftelse'
                ? `satt av ${navnAv(o.scores.setBy)} i møtet`
                : `${navnAv(o.scores.setBy)} i møtet`
          }`}</span>
        </p>
      ) : tallSynlig && o && o.outcome !== 'ikke_i_dag' && !smal ? (
        <p className="ml-rad-linje hb-dempet">Ingen tall satt</p>
      ) : null}
      {endrer ? <EndreRad i={i} m={m} config={config} onLukk={onLukkEndre} /> : null}
    </li>
  );
}

/** Det som hører til utfallet: eier, kode, svaret — og om svaret er sendt. */
function UtfallDetalj({ i, o, m, config, bekreftet }: { i: DecisionMeetingItem; o: MeetingOutcome; m: DecisionMeeting; config: HypothesisConfig | undefined; bekreftet: boolean }) {
  const svar = bekreftet && i.transitionAt ? `svar sendt ${kortDatoAv(i.transitionAt)}` : null;
  if (o.outcome === 'velg') return <span className="hb-dempet">{[o.owner ? `${navnAv(o.owner)} eier testen` : null, svar].filter(Boolean).join(' · ')}</span>;
  if (o.outcome === 'parker') return <span className="hb-dempet">{svar ?? ''}</span>;
  if (o.outcome === 'avvis') {
    const kode = kodeNavn(config, o.rejectReason);
    return (
      <span className="hb-dempet">
        {kode ?? ''}
        {i.state === 'venter_begrunnelse' ? (
          <span className="ml-mangler">
            <Ikon navn="warning-outline" />
            {`begrunnelse mangler — ${navneliste(innsendere(i.hypothesis))} ser «Vurderes» til den er skrevet`}
          </span>
        ) : !o.response && !bekreftet ? (
          <span className="ml-mangler">
            <Ikon navn="warning-outline" />
            {`begrunnelsen skrives av ${navnAv(m.registeredBy)}`}
          </span>
        ) : svar ? (
          ` · ${svar}`
        ) : null}
      </span>
    );
  }
  const frist = fristKort(i.hypothesis);
  return <span className="hb-dempet">{bekreftet ? `står i Vurderes${frist ? ` med frist ${frist}` : ''} · ingen svar` : `står i Vurderes med frist ${frist ?? '—'} · ingen svar går ut`}</span>;
}

/** «Endre» (W9): hele raden lagres på én gang som beslutningstakerens; det registrerte står ved siden av. */
function EndreRad({ i, m, config, onLukk }: { i: DecisionMeetingItem; m: DecisionMeeting; config: HypothesisConfig | undefined; onLukk: () => void }) {
  const skriv = useMoeteSkriving(m.id);
  const o = gjeldende(i);
  const [v, setV] = useState<Utfallsverdier>(() => ({
    outcome: o?.outcome,
    ownerId: o?.owner?.id ?? null,
    revisitAt: o?.revisitAt ?? null,
    rejectReason: o?.rejectReason ?? null,
    relatedHypothesisId: o?.relatedHypothesis?.id ?? null,
    response: o?.response ? { text: o.response.text, ...(o.response.templateId ? { templateId: o.response.templateId } : {}) } : null,
    impact: o?.scores?.impact ?? null,
    confidence: o?.scores?.confidence ?? null,
    effort: o?.scores?.effort ?? null,
  }));
  const [feil, setFeil] = useState<string | null>(null);
  const nr = nummerTekst(i.hypothesis.number);
  const testetI = useHypoteseListe({ statusType: 'complete' }, v.rejectReason === 'vurdert_for');

  async function lagre() {
    setFeil(null);
    const body: MeetingDecisionUpdate = {
      ...(v.outcome ? { outcome: v.outcome } : {}),
      ownerId: v.outcome === 'velg' ? (v.ownerId ?? null) : null,
      revisitAt: v.outcome === 'parker' ? (v.revisitAt ?? null) : null,
      rejectReason: v.outcome === 'avvis' ? ((v.rejectReason as MeetingDecisionUpdate['rejectReason']) ?? null) : null,
      ...(v.outcome === 'avvis' && v.rejectReason === 'vurdert_for' ? { relatedHypothesisId: v.relatedHypothesisId ?? null } : {}),
      response: v.outcome === 'ikke_i_dag' ? null : (v.response ?? null),
      ...(config?.scoresEnabled ? { impact: v.impact ?? null, confidence: v.confidence ?? null, effort: v.effort ?? null } : {}),
    };
    try {
      await skriv.mutateAsync({ handling: 'endreBeslutning', hypoteseId: i.hypothesis.id, body });
      onLukk();
    } catch (e) {
      setFeil(erApiFeil(e) ? (erNettfeil(e) ? 'Ikke lagret — ingen nett. Det du valgte står.' : e.brukertekst) : 'Noe gikk galt. Prøv igjen.');
    }
  }

  return (
    <form
      className="ml-endre"
      aria-label={`Endre ${nr}`}
      onSubmit={(e) => {
        e.preventDefault();
        void lagre();
      }}
    >
      <UtfallFelter
        id={`ml-${i.hypothesis.number}`}
        etikett="Utfall"
        verdier={v}
        config={config}
        decider={m.decider}
        innsender={i.hypothesis.submittedBy}
        medlemmer={config?.members ?? []}
        tall={Boolean(config?.scoresEnabled)}
        testetI={testetI.data?.pages.flatMap((p) => p.items)}
        foreslaattEier={i.proposedOwner}
        onEndre={(e) => setV((x) => ({ ...x, ...e }))}
      />
      {feil ? <Varsel>{feil}</Varsel> : null}
      <div className="panel-handlinger">
        <button type="submit" className="panel-knapp" aria-busy={skriv.isPending || undefined}>
          Lagre endringen
        </button>
        <Tekstknapp onClick={onLukk}>Avbryt</Tekstknapp>
      </div>
    </form>
  );
}

/** «Gi videre …» (kjerne 5.3: en tildeling, ikke en avgjørelse) — én rad, eller hele møtet. */
function GiVidereDialog({ m, rad, config, onLukk }: { m: DecisionMeeting; rad: DecisionMeetingItem | null; config: HypothesisConfig | undefined; onLukk: () => void }) {
  const skriv = useMoeteSkriving(m.id);
  const andre = (config?.deciders ?? []).filter((d) => d.id !== m.decider.id);
  const [til, setTil] = useState<string>(andre[0]?.id ?? '');
  const [feil, setFeil] = useState<string | null>(null);
  const tittel = rad ? `Gi ${nummerTekst(rad.hypothesis.number)} videre` : 'Gi hele møtet videre';
  const navn = navnAv(andre.find((d) => d.id === til));
  const knapp = useRef<HTMLButtonElement>(null);

  async function gi() {
    if (!til) return;
    setFeil(null);
    try {
      await skriv.mutateAsync(rad ? { handling: 'giVidereRad', hypoteseId: rad.hypothesis.id, deciderId: til } : { handling: 'giVidere', deciderId: til });
      onLukk();
    } catch (e) {
      setFeil(erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.');
    }
  }

  return (
    <Dialog tittelId="ml-videre-h" onLukk={onLukk} testid="ml-videre">
      <h2 id="ml-videre-h" className="panel-h2">
        {tittel}
      </h2>
      {andre.length ? (
        <label className="panel-felt">
          <span>Til</span>
          <select value={til} onChange={(e) => setTil(e.target.value)}>
            {andre.map((d) => (
              <option key={d.id} value={d.id}>
                {navnAv(d)}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p className="panel-tekst">Det står ingen andre på beslutningstakerlista. Eier legger til flere i rommets Innstillinger.</p>
      )}
      <p className="panel-tekst">{rad ? `Raden flyttes til et nytt møte med samme navn og dato. ${navn} bekrefter den der. Ingen begrunnelse trengs.` : `${navn} bekrefter hele lista i stedet for deg. Ingen begrunnelse trengs.`}</p>
      {feil ? <Varsel>{feil}</Varsel> : null}
      <div className="panel-handlinger">
        <button ref={knapp} type="button" className="panel-knapp" disabled={!andre.length} aria-busy={skriv.isPending || undefined} onClick={() => void gi()}>
          Gi videre
        </button>
        <Knapp label="Avbryt" kbd="Esc" lenke onClick={onLukk} />
      </div>
    </Dialog>
  );
}

/**
 * «Skriv begrunnelsen» (W11, P8): avvisningen er bekreftet, men innsenderen ser «Vurderes» til teksten
 * finnes. Malen er kodens og må skrives om (DD-09) — knappen sier det før kallet.
 */
function BegrunnelseDialog({ m, rad, config, onLukk }: { m: DecisionMeeting; rad: DecisionMeetingItem; config: HypothesisConfig | undefined; onLukk: () => void }) {
  const skriv = useMoeteSkriving(m.id);
  const o = gjeldende(rad);
  const innsender = navneliste(innsendere(rad.hypothesis));
  const mal = malFor(config, 'avvis', { kode: o?.rejectReason, navn: innsender })?.text ?? '';
  const [tekst, setTekst] = useState(mal);
  const [feil, setFeil] = useState<string | null>(null);
  const nr = nummerTekst(rad.hypothesis.number);
  const grunn = !tekst.trim() ? 'Skriv begrunnelsen først.' : tekst.trim() === mal.trim() ? 'Skriv om malen før du sender.' : null;

  async function send() {
    if (grunn) return;
    setFeil(null);
    try {
      await skriv.mutateAsync({ handling: 'begrunnelse', hypoteseId: rad.hypothesis.id, response: { text: tekst } });
      onLukk();
    } catch (e) {
      setFeil(erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.');
    }
  }

  return (
    <Dialog tittelId="ml-begrunn-h" onLukk={onLukk} testid="ml-begrunn">
      <h2 id="ml-begrunn-h" className="panel-h2">
        {`Begrunnelse for ${nr}`}
      </h2>
      <p className="panel-tekst">{`${stor(kodeNavn(config, o?.rejectReason) ?? 'Avvist')}. ${innsender} ser «Vurderes» til begrunnelsen er sendt; da skjer avvisningen.`}</p>
      <label className="panel-felt">
        <span>{`Svar til ${innsender}`}</span>
        <textarea rows={5} maxLength={500} value={tekst} onChange={(e) => setTekst(e.target.value)} />
      </label>
      {feil ? <Varsel>{feil}</Varsel> : null}
      <div className="panel-handlinger">
        <span className="panel-knapp-med-grunn">
          <button type="button" className="panel-knapp" aria-disabled={grunn ? true : undefined} aria-describedby={grunn ? 'ml-begrunn-grunn' : undefined} aria-busy={skriv.isPending || undefined} onClick={grunn ? undefined : () => void send()}>
            {grunn ? <Ikon navn="lock-closed-outline" /> : null}
            Send begrunnelsen
          </button>
          {grunn ? (
            <span id="ml-begrunn-grunn" className="h-grunn">
              {grunn}
            </span>
          ) : null}
        </span>
        <Knapp label="Avbryt" kbd="Esc" lenke onClick={onLukk} />
      </div>
    </Dialog>
  );
}

/** Møtets egen tidslinje (W11 «Hendelser»), nyeste først. Navn og nummer, aldri tekst. */
function Hendelser({ m, megId }: { m: DecisionMeeting; megId: string | undefined }) {
  const hvem = (e: MeetingEvent) => (e.by.id === megId ? 'Du' : navnAv(e.by));
  const ikkeIDag = aktiveRader(m)
    .filter((i) => gjeldende(i)?.outcome === 'ikke_i_dag')
    .map((i) => nummerTekst(i.hypothesis.number));
  const linje = (e: MeetingEvent): { ikon: string; tekst: string; detalj?: string } => {
    const nr = e.hypothesisNumber !== undefined ? nummerTekst(e.hypothesisNumber) : '';
    switch (e.kind) {
      case 'opprettet':
        return { ikon: 'calendar-outline', tekst: `${hvem(e)} opprettet møtet med ${navnAv(m.decider)} som beslutningstaker` };
      case 'startet':
        return { ikon: 'tv-outline', tekst: `${hvem(e)} startet møtet` };
      case 'sendt':
        return { ikon: 'send-outline', tekst: `${hvem(e)} avsluttet møtet og sendte lista til ${navnAv(m.decider)}`, ...(ikkeIDag.length ? { detalj: `${navneliste(ikkeIDag)} fikk «Ikke i dag».` } : {}) };
      case 'rad_endret':
        return { ikon: 'create-outline', tekst: `${hvem(e)} endret ${nr}: ${e.fromOutcome ? utfallOppforing(e.fromOutcome).etikett : '—'} → ${e.toOutcome ? utfallOppforing(e.toOutcome).etikett : '—'}` };
      case 'gitt_videre':
        return { ikon: 'arrow-redo-outline', tekst: `${hvem(e)} ga ${nr || 'møtet'} videre til ${navnAv(e.to)}` };
      case 'bekreftet': {
        const fikk = aktiveRader(m).filter((i) => i.state === 'bekreftet' && i.transitionAt && i.transitionAt <= e.at).length;
        const venter = aktiveRader(m).filter((i) => i.state === 'venter_begrunnelse').map((i) => nummerTekst(i.hypothesis.number));
        return { ikon: 'checkmark-circle-outline', tekst: `${hvem(e)} bekreftet møtet`, detalj: [fikk ? `${stor(somOrd(fikk))} ${fikk === 1 ? 'svar' : 'svar'} gikk ut.` : null, venter.length ? `${navneliste(venter)} venter på begrunnelse.` : null].filter(Boolean).join(' ') || undefined };
      }
      case 'begrunnelse_skrevet':
        return { ikon: 'create-outline', tekst: `${hvem(e)} skrev begrunnelsen for ${nr}` };
      default:
        return { ikon: 'ellipse-outline', tekst: hvem(e) };
    }
  };
  return (
    <section className="ml-hendelser" aria-labelledby="ml-hendelser-h">
      <h3 id="ml-hendelser-h" className="hb-seksjon-tittel">
        Hendelser
      </h3>
      <ol className="hb-logg" aria-label="Hendelser">
        {m.events.map((e, n) => {
          const l = linje(e);
          return (
            <li key={`${e.kind}-${e.at}-${n}`} className="hb-logg-rad ml-hendelse">
              <span className="hb-fet">
                <Ikon navn={l.ikon} /> {l.tekst}
              </span>
              <span className="hb-liten">{`${kortDatoAv(e.at)} ${klokke(e.at)}`}</span>
              {l.detalj ? <span className="hb-dempet hb-logg-detalj">{l.detalj}</span> : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export type { MeetingOutcome };
